/**
 * O botão Feedback, no topo do painel e do portal — spec 0108 (FR-001, FR-011).
 *
 * Ao clicar, tira a captura da tela ANTES de abrir a gaveta, para ela não
 * sair na foto. O número é o de respostas da equipe que a pessoa não leu,
 * lido a cada dois minutos com a aba do navegador visível.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquarePlus } from 'lucide-react';
import { lerResumo } from './api';
import { capturarTela } from './captura';
import { GavetaFeedback, type EstadoDaCaptura } from './GavetaFeedback';
import './feedback.css';

const INTERVALO_MS = 120_000;

interface Props {
  /** O caminho da tela no menu, ou a aba do portal (FR-005). */
  tela: string;
  className?: string;
}

export function BotaoFeedback({ tela, className }: Props) {
  const [aberta, setAberta] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [captura, setCaptura] = useState<EstadoDaCaptura>({ tipo: 'removida' });
  const [naoLidas, setNaoLidas] = useState(0);
  const [abaInicial, setAbaInicial] = useState<'enviar' | 'meus'>('enviar');
  const botao = useRef<HTMLButtonElement>(null);

  const atualizar = useCallback(() => {
    lerResumo().then(setNaoLidas).catch(() => undefined);
  }, []);

  useEffect(() => {
    atualizar();
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') atualizar(); }, INTERVALO_MS);
    const aoVoltar = () => { if (document.visibilityState === 'visible') atualizar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [atualizar]);

  const abrir = async () => {
    if (preparando) return;
    setPreparando(true);
    // Com resposta nova, abre direto onde ela está.
    setAbaInicial(naoLidas > 0 ? 'meus' : 'enviar');
    let blob: Blob | null = null;
    try {
      blob = await capturarTela();
    } catch {
      blob = null;
    }
    setCaptura(blob ? { tipo: 'pronta', blob } : { tipo: 'falhou' });
    setPreparando(false);
    setAberta(true);
  };

  const fechar = () => {
    setAberta(false);
    atualizar();
    requestAnimationFrame(() => botao.current?.focus());
  };

  const rotulo = naoLidas > 0
    ? `Feedback, ${naoLidas} ${naoLidas === 1 ? 'resposta não lida' : 'respostas não lidas'}`
    : 'Feedback';

  return (
    <>
      <button
        ref={botao}
        type="button"
        className={`fb-botao${className ? ` ${className}` : ''}`}
        onClick={abrir}
        aria-label={rotulo}
        aria-busy={preparando || undefined}
        data-sem-captura
      >
        {preparando
          ? <Loader2 size={16} aria-hidden="true" className="fb-girando" />
          : <MessageSquarePlus size={16} aria-hidden="true" />}
        <span className="fb-botao-texto">{preparando ? 'Preparando…' : 'Feedback'}</span>
        {naoLidas > 0 && <span className="fb-numero" aria-hidden="true">{naoLidas}</span>}
      </button>
      <GavetaFeedback
        aberta={aberta}
        onFechar={fechar}
        captura={captura}
        onCaptura={setCaptura}
        tela={tela}
        onLeu={atualizar}
        abaInicial={abaInicial}
      />
    </>
  );
}
